import {
  RadioGroup,
  RadioGroupItem,
} from "@arcadeai/design-system/components/ui/atoms/radio-group";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@arcadeai/design-system/rhf";
import { Link } from "@tanstack/react-router";
import { Suspense } from "react";
import type { Control } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import type { McpServerAuthType, McpServerCreateRequest } from "../../types";
import { UserSourceSelector } from "./user-source-selector";

type AuthTypeFormFieldProps = {
  control: Control<McpServerCreateRequest>;
};

type RadioOptionProps = {
  id: string;
  value: string;
  label: React.ReactNode;
  selected: boolean;
  children?: React.ReactNode;
};

function RadioOption({
  id,
  value,
  label,
  selected,
  children,
}: RadioOptionProps) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <RadioGroupItem id={id} value={value} />
        <label className="cursor-pointer font-medium text-sm" htmlFor={id}>
          {label}
        </label>
      </div>
      <div
        className={cn(
          "grid pl-7 transition-all duration-200",
          selected ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

function AuthTypeDescription({ authType }: { authType?: McpServerAuthType }) {
  if (authType === "arcade_header") {
    return (
      <div className="space-y-1">
        <span>Pass authentication via request headers:</span>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>
            <strong>Authorization</strong> — with "Bearer &#123;
            <Link
              className="text-link hover:underline"
              from="/orgs/$organizationId/projects/$projectId/api-keys"
              to="."
            >
              your_api_key
            </Link>
            &#125;"
          </li>
          <li>
            <strong>Arcade-User-ID</strong> — the email of the end-user of your
            agent
          </li>
        </ul>
        <span>
          Use this method if you don't want your end-users to authenticate via
          Arcade.
        </span>
      </div>
    );
  }

  if (authType === "user_source") {
    return (
      <>
        Users authenticate via your OIDC identity provider. Recommended for
        non-Arcade users.
      </>
    );
  }

  return (
    <>
      Users authenticate with their Arcade account via OAuth. Recommended for
      development, testing, and internal use.
    </>
  );
}

export function AuthTypeFormField({ control }: AuthTypeFormFieldProps) {
  const { setValue } = useFormContext<McpServerCreateRequest>();

  return (
    <FormField
      control={control}
      name="auth_type"
      render={({ field }) => {
        const topLevel = field.value === "arcade" ? "arcade" : "non_arcade";

        const setAuthType = (type: McpServerAuthType) => {
          if (type !== "user_source") {
            setValue("user_source_id", null);
          }
          field.onChange(type);
        };

        return (
          <FormItem>
            <FormLabel>Who are the users of this Gateway?</FormLabel>
            <RadioGroup
              className="gap-3"
              onValueChange={(val) => {
                if (val === "arcade") {
                  setAuthType("arcade");
                } else if (field.value === "arcade") {
                  setAuthType("user_source");
                }
              }}
              value={topLevel}
            >
              <RadioOption
                id="auth-top-arcade"
                label={
                  <>
                    Members of this Project{" "}
                    <span className="font-normal text-muted-foreground">
                      (Arcade Auth)
                    </span>
                  </>
                }
                selected={topLevel === "arcade"}
                value="arcade"
              >
                <FormDescription className="pt-1">
                  <AuthTypeDescription authType="arcade" />
                </FormDescription>
              </RadioOption>

              <RadioOption
                id="auth-top-non-arcade"
                label="Non-Arcade Users"
                selected={topLevel === "non_arcade"}
                value="non_arcade"
              >
                <p className="mt-2 mb-1 font-medium text-foreground text-sm">
                  How will users authenticate?
                </p>
                <RadioGroup
                  className="gap-3"
                  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                  onValueChange={(val) => setAuthType(val as McpServerAuthType)}
                  value={field.value ?? "user_source"}
                >
                  <RadioOption
                    id="auth-sub-user-source"
                    label={
                      <>
                        User Source{" "}
                        <span className="font-normal text-muted-foreground">
                          (recommended)
                        </span>
                      </>
                    }
                    selected={field.value === "user_source"}
                    value="user_source"
                  >
                    <FormDescription className="pt-1">
                      <AuthTypeDescription authType="user_source" />
                    </FormDescription>
                    {field.value === "user_source" && (
                      <div className="pt-2">
                        <Suspense
                          fallback={<Skeleton className="h-10 w-full" />}
                        >
                          <UserSourceSelector control={control} />
                        </Suspense>
                      </div>
                    )}
                  </RadioOption>

                  <RadioOption
                    id="auth-sub-arcade-header"
                    label={
                      <>
                        Arcade Headers{" "}
                        <span className="font-normal text-muted-foreground">
                          (fallback)
                        </span>
                      </>
                    }
                    selected={field.value === "arcade_header"}
                    value="arcade_header"
                  >
                    <FormDescription className="pt-1">
                      <AuthTypeDescription authType="arcade_header" />
                    </FormDescription>
                  </RadioOption>
                </RadioGroup>
              </RadioOption>
            </RadioGroup>
          </FormItem>
        );
      }}
    />
  );
}
